"use client";

import { useEffect } from "react";
import type { AnimationMode, AnimationTrigger } from "./shopStorefrontCore";

export const shopAnimationDomSelector = "[data-shop-animation], [style*=\"--shop-animation-name\"], [style*=\"animation-name\"]";

export const shopObservedAnimationDomSelector = shopAnimationDomSelector;

export function useReplayAppearanceAnimations(refreshKey: unknown, replayAllAnimations: boolean): void {
    useEffect(() => {
        const selector = replayAllAnimations ? shopAnimationDomSelector : shopObservedAnimationDomSelector;
        const documents: Document[] = [document];
        const cleanupCallbacks: Array<() => void> = [];
        const queuedReplays = new WeakMap<HTMLElement, () => void>();
        const isAnimatedElement = (element: HTMLElement): boolean => {
            if (element.dataset.shopAnimation) {
                return true;
            }

            const inlineCustomName = element.style.getPropertyValue("--shop-animation-name").trim();
            const inlineAnimationName = element.style.animationName.trim();

            if (inlineCustomName || (inlineAnimationName && inlineAnimationName !== "none")) {
                return true;
            }

            return false;
        };

        document.querySelectorAll<HTMLIFrameElement>("iframe").forEach((frame) => {
            try {
                const frameDocument = frame.contentDocument;

                if (frameDocument) {
                    documents.push(frameDocument);
                }
            } catch {
                // Cross-origin previews are ignored; same-origin editor frames are handled above.
            }
        });

        const elementsByDocument = new Map<Document, HTMLElement[]>();

        documents.forEach((animationDocument) => {
            const elements = Array.from(animationDocument.querySelectorAll<HTMLElement>(selector))
                .filter(isAnimatedElement);

            if (elements.length > 0) {
                elementsByDocument.set(animationDocument, elements);
            }
        });

        const elements = Array.from(elementsByDocument.values()).flat();

        if (elements.length === 0) {
            return undefined;
        }

        const animationModeForElement = (element: HTMLElement): AnimationMode => (
            element.dataset.shopAnimationMode === "scroll"
            || element.style.getPropertyValue("--shop-animation-mode").trim() === "scroll"
                ? "scroll"
                : "appearance"
        );
        const animationTriggerForElement = (element: HTMLElement): AnimationTrigger => (
            element.dataset.shopAnimationTrigger === "always"
            || element.style.getPropertyValue("--shop-animation-trigger").trim() === "always"
                ? "always"
                : "once"
        );
        const isElementVisuallyAvailable = (element: HTMLElement): boolean => {
            const view = element.ownerDocument.defaultView ?? window;
            let parent = element.parentElement;

            while (parent) {
                const style = view.getComputedStyle(parent);
                const opacity = Number(style.opacity);

                if (style.display === "none" || style.visibility === "hidden" || (opacity <= 0.01 && !isAnimatedElement(parent))) {
                    return false;
                }

                parent = parent.parentElement;
            }

            return true;
        };
        const elementDepth = (element: HTMLElement): number => {
            let depth = 0;
            let parent = element.parentElement;

            while (parent) {
                depth += 1;
                parent = parent.parentElement;
            }

            return depth;
        };
        const cssTimeValueMs = (value: string): number => {
            const trimmed = value.trim();

            if (trimmed.endsWith("ms")) {
                return Number(trimmed.slice(0, -2)) || 0;
            }

            if (trimmed.endsWith("s")) {
                return (Number(trimmed.slice(0, -1)) || 0) * 1000;
            }

            return Number(trimmed) || 0;
        };
        const cssTimeListMs = (value: string): number[] => value
            .split(",")
            .map(cssTimeValueMs)
            .filter((time) => Number.isFinite(time));
        const animationTotalMs = (element: HTMLElement): number => {
            const view = element.ownerDocument.defaultView ?? window;
            const style = view.getComputedStyle(element);
            const durations = cssTimeListMs(style.animationDuration);
            const delays = cssTimeListMs(style.animationDelay);
            const count = Math.max(durations.length, delays.length, 1);
            let total = 0;

            for (let index = 0; index < count; index += 1) {
                total = Math.max(total, (durations[index] ?? durations[0] ?? 0) + (delays[index] ?? delays[0] ?? 0));
            }

            return total;
        };
        const nearestAnimatedAncestor = (element: HTMLElement): HTMLElement | null => {
            let parent = element.parentElement;

            while (parent) {
                if (isAnimatedElement(parent)) {
                    return parent;
                }

                parent = parent.parentElement;
            }

            return null;
        };
        const dispatchAnimationComplete = (element: HTMLElement) => {
            const view = element.ownerDocument.defaultView ?? window;

            element.dispatchEvent(new view.CustomEvent("shop-animation-cycle-complete", { bubbles: false }));
        };
        const replay = (element: HTMLElement) => {
            queuedReplays.get(element)?.();
            const animationName = element.style.animationName;
            const view = element.ownerDocument.defaultView ?? window;
            let completed = false;
            let timeoutId = 0;

            const complete = () => {
                if (completed) {
                    return;
                }

                completed = true;
                element.classList.remove("shop-animation-running");
                element.classList.add("shop-animation-settled");
                element.removeEventListener("animationend", handleAnimationEnd);
                if (timeoutId !== 0) {
                    view.clearTimeout(timeoutId);
                }
                dispatchAnimationComplete(element);
            };
            const handleAnimationEnd = (event: AnimationEvent) => {
                if (event.target === element) {
                    complete();
                }
            };

            element.classList.add("shop-animation-running");
            element.classList.add("shop-animation-played");
            element.classList.add("shop-animation-restarting");
            element.classList.remove("shop-animation-settled");
            element.classList.remove("shop-animation-visible");
            element.style.removeProperty("clip-path");
            element.style.animationName = "none";
            void element.offsetWidth;
            element.classList.remove("shop-animation-restarting");
            if (animationName) {
                element.style.animationName = animationName;
            } else {
                element.style.removeProperty("animation-name");
            }
            element.classList.add("shop-animation-visible");
            element.addEventListener("animationend", handleAnimationEnd);
            timeoutId = view.setTimeout(complete, Math.max(80, animationTotalMs(element) + 80));
            cleanupCallbacks.push(() => {
                element.removeEventListener("animationend", handleAnimationEnd);
                view.clearTimeout(timeoutId);
            });
        };
        const replayAfterAncestor = (element: HTMLElement, ancestor: HTMLElement) => {
            const view = element.ownerDocument.defaultView ?? window;
            let done = false;
            let timeoutId = 0;
            const cleanup = () => {
                if (done) {
                    return;
                }

                done = true;
                ancestor.removeEventListener("shop-animation-cycle-complete", start);
                if (timeoutId !== 0) {
                    view.clearTimeout(timeoutId);
                }
                queuedReplays.delete(element);
            };
            const start = () => {
                cleanup();
                replay(element);
            };

            queuedReplays.get(element)?.();
            element.classList.remove("shop-animation-settled");
            element.classList.remove("shop-animation-visible");
            ancestor.addEventListener("shop-animation-cycle-complete", start, { once: true });
            timeoutId = view.setTimeout(start, Math.max(140, animationTotalMs(ancestor) + 120));
            queuedReplays.set(element, cleanup);
            cleanupCallbacks.push(cleanup);
        };
        const replayWhenReady = (element: HTMLElement) => {
            const ancestor = nearestAnimatedAncestor(element);

            if (
                ancestor
                && (
                    ancestor.classList.contains("shop-animation-running")
                    || ancestor.classList.contains("shop-animation-restarting")
                    || !ancestor.classList.contains("shop-animation-visible")
                )
            ) {
                replayAfterAncestor(element, ancestor);

                return;
            }

            replay(element);
        };
        const showOrReplayElement = (element: HTMLElement, forceReplay = false) => {
            const mode = animationModeForElement(element);
            const trigger = animationTriggerForElement(element);

            if (!isElementVisuallyAvailable(element)) {
                queuedReplays.get(element)?.();

                if (mode === "appearance" || trigger === "always") {
                    element.classList.remove("shop-animation-settled");
                    element.classList.remove("shop-animation-visible");
                }

                if (trigger === "always") {
                    element.classList.remove("shop-animation-played");
                }

                return;
            }

            if (forceReplay || !element.classList.contains("shop-animation-played")) {
                replayWhenReady(element);
            } else {
                element.classList.add("shop-animation-visible");
            }
        };
        const intersectingElements = new Set<HTMLElement>();
        const checkIntersectingElements = () => {
            intersectingElements.forEach((element) => showOrReplayElement(element));
        };
        const mutationObservers: MutationObserver[] = [];
        let visibilityFrame = 0;
        let earlyVisibilityTimeout = 0;
        let lateVisibilityTimeout = 0;
        const scheduleVisibilityCheck = (animationDocument: Document) => {
            const view = animationDocument.defaultView ?? window;

            if (visibilityFrame === 0) {
                visibilityFrame = view.requestAnimationFrame(() => {
                    visibilityFrame = 0;
                    checkIntersectingElements();
                });
            }

            if (earlyVisibilityTimeout !== 0) {
                view.clearTimeout(earlyVisibilityTimeout);
            }

            if (lateVisibilityTimeout !== 0) {
                view.clearTimeout(lateVisibilityTimeout);
            }

            earlyVisibilityTimeout = view.setTimeout(checkIntersectingElements, 90);
            lateVisibilityTimeout = view.setTimeout(checkIntersectingElements, 560);
        };
        const observers: IntersectionObserver[] = [];

        elementsByDocument.forEach((documentElements, animationDocument) => {
            const Observer = animationDocument.defaultView?.IntersectionObserver ?? globalThis.IntersectionObserver;

            documentElements.forEach((element) => {
                element.classList.remove("shop-animation-running");
                element.classList.remove("shop-animation-restarting");
                element.classList.remove("shop-animation-played");
                element.classList.remove("shop-animation-settled");

                if (animationModeForElement(element) === "appearance" || animationTriggerForElement(element) === "always") {
                    element.classList.remove("shop-animation-visible");
                }
            });

            const MutationObserverCtor = animationDocument.defaultView?.MutationObserver ?? globalThis.MutationObserver;

            if (MutationObserverCtor && animationDocument.body) {
                const mutationObserver = new MutationObserverCtor(() => scheduleVisibilityCheck(animationDocument));

                mutationObserver.observe(animationDocument.body, {
                    attributeFilter: ["class", "hidden", "style"],
                    attributes: true,
                    subtree: true,
                });
                mutationObservers.push(mutationObserver);
            }

            if (!Observer) {
                documentElements.forEach((element) => {
                    element.classList.add("shop-animation-visible");
                    element.classList.add("shop-animation-settled");
                });

                return;
            }

            const observer = new Observer((entries) => {
                [...entries].sort((first, second) => (
                    elementDepth(first.target as HTMLElement) - elementDepth(second.target as HTMLElement)
                )).forEach((entry) => {
                    const element = entry.target as HTMLElement;
                    const mode = animationModeForElement(element);
                    const trigger = animationTriggerForElement(element);

                    if (entry.isIntersecting) {
                        intersectingElements.add(element);
                        showOrReplayElement(element, replayAllAnimations);

                        return;
                    }

                    intersectingElements.delete(element);
                    queuedReplays.get(element)?.();

                    if (mode === "scroll" || trigger === "always") {
                        element.classList.remove("shop-animation-played");
                        element.classList.remove("shop-animation-settled");
                        element.classList.remove("shop-animation-visible");
                    }
                });
            }, { threshold: 0.18 });

            documentElements.forEach((element) => observer.observe(element));
            observers.push(observer);
        });

        return () => {
            const view = document.defaultView ?? window;

            if (visibilityFrame !== 0) {
                view.cancelAnimationFrame(visibilityFrame);
            }

            if (earlyVisibilityTimeout !== 0) {
                view.clearTimeout(earlyVisibilityTimeout);
            }

            if (lateVisibilityTimeout !== 0) {
                view.clearTimeout(lateVisibilityTimeout);
            }

            cleanupCallbacks.forEach((cleanup) => cleanup());
            mutationObservers.forEach((observer) => observer.disconnect());
            observers.forEach((observer) => observer.disconnect());
        };
    }, [refreshKey, replayAllAnimations]);
}
